import { AppStateSlice } from '$tablet-app/src/app/stores/app_state';
import {
  submitForgotLockPIN,
  updateLockPin,
  validateLockPin,
} from '@akin-home/lock-screen/frontend';
import { setAppIsLocked } from '../functions/setAppIsLocked';
import { setAppUnlocked } from '../functions/setAppUnlocked';
import { LockScreenMode, LockScreenStore } from './types';

export const createLockScreenStore: AppStateSlice<LockScreenStore> = (
  set,
  get
) => ({
  lock_screen: {
    input: '',
    async inputNumber(number: number) {
      const input = `${get().lock_screen.input}${number}`;
      const checking = input.length === 4;

      set((state) => {
        state.lock_screen.input = input;
        state.lock_screen.checking = checking;

        if (checking) {
          delete state.lock_screen.error;
        }
      });

      /** The number is not being checked, just return */
      if (!checking) return;

      const user_id = get().locked_user_id;
      const confirm_reset_pin = get().lock_screen.confirm_reset_pin;
      const mode = get().lock_screen.mode;

      if (!user_id) return;

      /** Resetting of PIN is being confirmed and matches */
      if (confirm_reset_pin && input === get().lock_screen.reset_pin_input) {
        await updateLockPin({
          user_id,
          pin: input,
        });

        get().lock_screen.unlock();

        return;
      } else if (
        // Resetting of pin is incorrect
        confirm_reset_pin &&
        input !== get().lock_screen.reset_pin_input
      ) {
        get().lock_screen.promptPinReset({
          error: 'PIN did not match. Enter again.',
        });
        return;
      } else if (get().lock_screen.prompt_reset_pin) {
        get().lock_screen.promptPinReset({
          confirm_reset: true,
        });
        return;
      }

      const validation = await validateLockPin({
        user_id,
        pin: get().lock_screen.input,
      });

      const valid = validation.success;

      if (!valid) {
        set((state) => {
          state.lock_screen.input = '';
          state.lock_screen.error = 'That PIN is incorrect';
        });
        return;
      }

      if (validation.prompt_new_pin) {
        get().lock_screen.promptPinReset();
        return;
      }

      if (mode === LockScreenMode.APP_LOCK) {
        get().lock_screen.unlock();
      } else if (mode === LockScreenMode.PROFILE_SWITCH && user_id) {
        get().setCurrentUserID(user_id);
      }
    },

    removeNumber() {
      set((state) => {
        const { input } = state.lock_screen;

        state.lock_screen.input =
          input.length === 0 ? input : input.slice(0, -1);
      });
    },

    close() {
      set((state) => {
        state.lock_screen.input = '';
        state.lock_screen.visible = false;
      });
    },

    unlock() {
      setAppUnlocked();

      set((state) => {
        state.lock_screen.input = '';
        state.lock_screen.visible = false;
        delete state.lock_screen.mode;
        delete state.locked_user_id;
      });
    },
    async forgotPin() {
      const locked_user_id = get().locked_user_id;

      if (!locked_user_id) return;

      const user_email = get().users?.find(
        (u) => u.id === locked_user_id
      )?.email;

      if (user_email) {
        const res = await submitForgotLockPIN({ user_id: locked_user_id });

        if (res.success) {
          set((state) => {
            state.lock_screen.input = '';
            state.lock_screen.forgot_pin = true;
            delete state.lock_screen.error;
          });
        }
      }
    },

    promptPinReset(params) {
      set((state) => {
        state.lock_screen.forgot_pin = false;
        state.lock_screen.prompt_reset_pin = true;
        state.lock_screen.checking = false;

        state.lock_screen.reset_pin_input = params?.confirm_reset
          ? state.lock_screen.input
          : undefined;

        state.lock_screen.confirm_reset_pin = params?.confirm_reset;
        state.lock_screen.input = '';
        state.lock_screen.error = params?.error;
      });
    },

    openForgotPinDialog() {
      set((state) => {
        state.lock_screen.forgot_pin_dialog_visible = true;
      });
    },

    closeForgotPinDialog() {
      set((state) => {
        delete state.lock_screen.forgot_pin_dialog_visible;
      });
    },
  },
  switchProfile(user_id) {
    set((state) => {
      state.lock_screen.visible = true;
      state.lock_screen.mode = LockScreenMode.PROFILE_SWITCH;
      state.lock_screen.user_id = user_id;
    });
  },

  async lockApp(profile_id: string) {
    await setAppIsLocked({ profile_id });

    set((state) => {
      state.lock_screen.visible = true;
      state.lock_screen.mode = LockScreenMode.APP_LOCK;
      state.locked_user_id = profile_id;
    });
  },
});
